import { FC, useCallback, MouseEventHandler } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';

import serverVars from 'server-vars';
import { usePrerenderedCheckout } from 'dibs-checkout/exports/prerender/usePrerenderedCheckout';
import { hidePage } from 'dibs-buyer-layout/exports/hidePage';
import { trackPurchaseClick, Location } from '../../tracking/actionButtonsTracking';

import { Button } from 'dibs-elements/exports/Button';

import { PdpSharedBuyItNowButton_item$key } from './__generated__/PdpSharedBuyItNowButton_item.graphql';

const itemFragment = graphql`
    fragment PdpSharedBuyItNowButton_item on Item
    @argumentDefinitions(pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: pdp }) {
        serviceId @required(action: THROW)
        ...PdpSharedShoppingButton_item
        ...actionButtonsTracking_item
            @arguments(fetchAuction: $fetchAuction, pageDisplayEnum: $pageDisplayEnum)
    }
`;

const forcedCurrency = serverVars.get('forcedCurrency');

const variantMap = {
    marketplace: 'primary',
    auction: 'secondary',
} as const;

export const PdpSharedBuyItNowButton: FC<{
    item: PdpSharedBuyItNowButton_item$key;
    skuId?: string;
    quantitySelected?: string;
    variant?: 'marketplace' | 'auction';
    disabled?: boolean;
    size?: 'small' | 'large';
    location?: Location;
}> = ({
    item: itemRef,
    skuId,
    quantitySelected,
    variant = 'marketplace',
    disabled,
    size = 'large',
    location,
}) => {
    const environment = useRelayEnvironment();
    const item = useFragment(itemFragment, itemRef);

    const { serviceId: itemId } = item;

    const { redirect: redirectToCheckout } = usePrerenderedCheckout({
        hideParentPage: hidePage,
        itemId,
        params: {
            skuId,
            quantity: quantitySelected,
            offerCurrency: forcedCurrency,
        },
    });

    const onClick: MouseEventHandler = useCallback(
        event => {
            trackPurchaseClick({
                item,
                event,
                location,
                environment,
                quantity: Number(quantitySelected),
            });
            redirectToCheckout();
        },
        [item, location, environment, quantitySelected, redirectToCheckout]
    );

    return (
        <Button
            fullWidth
            type={variantMap[variant]}
            size={size}
            onClick={onClick}
            disabled={disabled}
        >
            <FormattedMessage id="PdpSharedBuyItNowButton.buyItNow" defaultMessage="Buy it now" />
        </Button>
    );
};
